import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import type { Persona, WorkOption, OnboardingProps } from '../onboarding/types';
import { backgroundColorMap } from '../onboarding/config/personas';
import { workOptions } from '../onboarding/config/workOptions';
import { Step1, Step2, Step3, AvatarLabel, ApplicationPreview } from '../onboarding/components';
import { ShapeRenderer } from '../onboarding/shapes';


export default function OnboardingTemplate({ onComplete, facade, onCompleteOnboarding }: OnboardingProps = {}): JSX.Element {
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(1);
  const [selectedPersona, setSelectedPersona] = useState<Persona | null>(null);
  const [selectedWorkOption, setSelectedWorkOption] = useState<WorkOption | null>(null);
  const [prototyperMode, setPrototyperMode] = useState(false);

  const backgroundColor = selectedPersona ? backgroundColorMap[selectedPersona] : backgroundColorMap['Freelance'];

  const handleStep1Continue = () => {
    setCurrentStep(2);
  };

  const handleStep2Back = () => {
    setCurrentStep(1);
  };

  // Handle Step2 Continue - different behavior based on selected option
  const handleStep2Continue = async () => {
    // If "not-sure" is selected, mark onboarding complete and redirect to homepage
    if (selectedWorkOption === 'not-sure') {
      // Mark onboarding as complete since user made a conscious choice to skip Step3
      if (onCompleteOnboarding) {
        try {
          await onCompleteOnboarding();
        } catch (error) {
          console.error('Failed to complete onboarding for "not-sure" option:', error);
          // Continue anyway - don't block the user
        }
      }
      
      if (facade?.navigation?.toLanding) {
        facade.navigation.toLanding();
      } else if (typeof window !== 'undefined') {
        window.location.href = '/';
      }
      return;
    }
    
    // If third button is selected, enable prototyper mode
    if (selectedWorkOption === 'generate-solutions') {
      setPrototyperMode(true);
    } else {
      setPrototyperMode(false);
    }
    setCurrentStep(3);
  };

  const handleStep3Back = () => {
    setCurrentStep(2);
  };

  // Determine layout widths based on step
  const leftWidth = currentStep === 3 ? 'w-full lg:w-[70%]' : 'w-full lg:w-[40%]';
  const rightWidth = currentStep === 3 ? 'w-[30%]' : 'w-[60%]';

  return (
    <motion.div
      className="relative size-full h-[100vh] flex"
      animate={{ backgroundColor }}
      transition={{ duration: 0.6, ease: 'easeInOut' }}
    >
      {/* Main form */}
      <div
        className={`bg-white flex flex-col lg:px-24 lg:py-28 px-8 py-12 overflow-y-auto ${leftWidth} transition-all duration-1000`}
      >
        <AnimatePresence mode="wait">
          {currentStep === 1 ? (
            <Step1
              selectedPersona={selectedPersona}
              onSelectPersona={setSelectedPersona}
              onContinue={handleStep1Continue}
              isContinueDisabled={!selectedPersona}
            />
          ) : currentStep === 2 ? (
            <Step2
              selectedWorkOption={selectedWorkOption}
              onSelectWorkOption={setSelectedWorkOption}
              onBack={handleStep2Back}
              onContinue={handleStep2Continue}
            />
          ) : (
            <Step3
              selectedWorkOption={selectedWorkOption}
              onBack={handleStep3Back}
              facade={facade}
              prototyperMode={prototyperMode}
              onCompleteOnboarding={onCompleteOnboarding}
            />
          )}
        </AnimatePresence>
      </div>
      <div
        className={`${rightWidth} relative overflow-hidden transition-all duration-500 hidden lg:block`}
      >
        {/* Animated shapes - show in all steps, but only selected shape in step 3 */}
        <AnimatePresence mode="wait">
          <ShapeRenderer
            persona={selectedPersona || 'Freelance'}
            step={currentStep}
            selectedWorkOption={selectedWorkOption}
          />
        </AnimatePresence>
        {/* show avatar label only in step 1 when a persona is selected */}
        {currentStep === 1 && selectedPersona && (
          <div className="transition-all duration-1000">
            <AvatarLabel selected={selectedPersona} />
          </div>
        )}
        {/* Application preview - only show in step 2 */}
        {currentStep === 2 && <ApplicationPreview workOption={selectedWorkOption} />}
      </div>
    </motion.div>
  );
}
